import api from "./axios";
import type { Role, Permission } from "@/types/auth";

export type { Role, Permission };

export interface RoleFormData {
    name: string;
    guard_name: string;
    permissions?: number[];
}

export interface RoleListResponse {
    success: boolean;
    message: string;
    data: {
        items: Role[];
        meta: {
            current_page: number;
            last_page: number;
            per_page: number;
            total: number;
        };
    };
}

export interface RoleResponse {
    success: boolean;
    message: string;
    data: Role;
}

export interface PermissionGroupResponse {
    success: boolean;
    data: string[];
}

export interface PermissionByGroupResponse {
    success: boolean;
    data: {
        [group: string]: Permission[];
    };
}

const roleApi = {
    // Get all roles with pagination
    getRoles: async (params?: {
        page?: number;
        per_page?: number;
        search?: string;
    }): Promise<{
        success: boolean;
        message: string;
        data: { items: Role[]; meta: any };
    }> => {
        const response = await api.get("/v1/roles", { params });
        return response.data;
    },

    // Get role by ID
    getRole: async (id: number): Promise<RoleResponse> => {
        const response = await api.get(`/v1/roles/${id}`);
        return response.data;
    },

    // Create new role
    createRole: async (data: RoleFormData): Promise<RoleResponse> => {
        const response = await api.post("/v1/roles", data);
        return response.data;
    },

    // Update role
    updateRole: async (
        id: number,
        data: RoleFormData,
    ): Promise<RoleResponse> => {
        const response = await api.put(`/v1/roles/${id}`, data);
        return response.data;
    },

    // Delete role
    deleteRole: async (id: number): Promise<{ success: boolean }> => {
        const response = await api.delete(`/v1/roles/${id}`);
        return response.data;
    },

    // Assign permissions to role
    assignPermissions: async (
        id: number,
        permissionIds: number[],
    ): Promise<{ success: boolean }> => {
        const response = await api.post(`/v1/roles/${id}/permissions`, {
            permissions: permissionIds,
        });
        return response.data;
    },

    // Get role permissions
    getRolePermissions: async (
        id: number,
    ): Promise<{ success: boolean; data: Permission[] }> => {
        const response = await api.get(`/v1/roles/${id}/permissions`);
        return response.data;
    },
};

export default roleApi;
